import { useContext, useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, type DialogProps, DialogTitle, styled } from '@mui/material';
import axios, { type AxiosRequestConfig } from 'axios';
import { useSnackbar } from 'notistack';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useContainer } from 'unstated-next';

import { type RosterTableData } from './types';
import { UPDATE_CLASS_MEETING } from '../../config/apiPaths';
import { RosterContext } from '../../context/RosterContext';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { getUrlParams, getUrls } from '../../utils/url';
import { ClassContainer } from '../../views/ClassModal/ClassContainer';
import { StyledLoadingButton } from '../forms/LoadingButton';

const StyledDialog = styled(Dialog)`
    & .MuiDialog-paper {
        width: 500px;
    }

    & .MuiDialogTitle-root {
        background-color: ${(props) => props.theme.palette.primary.main};
        color: white;
    }

    & .MuiDialogTitle-root + .MuiDialogContent-root {
        padding-top: 16px;
    }
` as typeof Dialog;

const StyledDialogContent = styled(DialogContent)`
    padding: 0 1.5rem 1.5rem 1.5rem;

    p {
        margin-bottom: 1rem;
    }

    p:last-child {
        margin-bottom: 0;
    }
`;

export type RosterModalProps = DialogProps & {
    data: RosterTableData | undefined;
};

const RosterChangeHostModal = (props: RosterModalProps) => {
    const { data, ...other } = props;

    const { setOpenChangeHostModal } = useContext(RosterContext);
    const { setHostEmail, getClass } = useContainer(ClassContainer);

    const userUuid = getUrlParams().user_uuid ?? window.localStorage.getItem('user_uuid');
    const { classUuid } = useParams<{ classUuid: string }>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { api } = getUrls();

    const { t } = useTranslation('roster');
    const { enqueueSnackbar } = useSnackbar();

    const handleClose = () => {
        setOpenChangeHostModal(false);
    };

    const getUserNameValue = () => {
        if (data && data.user_name) return data.user_name;
    };

    const onSubmit = async () => {
        if (isLoading) return;

        if (!userUuid) {
            console.error('User uuid is not available');
            return;
        }

        setIsLoading(true);

        const payload = {
            class_uuid: classUuid,
            email: data?.email,
            student_id: data?.student_id
        };

        const config: AxiosRequestConfig = {
            headers: getUserUuidHeaders(userUuid)
        };

        try {
            await axios.post(`${api}${UPDATE_CLASS_MEETING}`, payload, config);
            await getClass();

            setIsLoading(false);

            setHostEmail(payload.email ?? '');

            setOpenChangeHostModal(false);
        } catch (error: any) {
            enqueueSnackbar(t('alert-content-10', { userName: getUserNameValue() }), { variant: 'error' });

            setOpenChangeHostModal(false);
        }
    };

    return (
        <StyledDialog {...other}>
            <DialogTitle>
                <Trans i18nKey="roster:make-name-lead-instructor">
                    Make <b>{{ name: getUserNameValue() }}</b> Lead Instructor (Host)
                </Trans>
            </DialogTitle>

            <StyledDialogContent>
                <p>
                    <Trans i18nKey="roster:change-host-text">
                        A new meeting will be created with <b>{{ name: getUserNameValue() }}</b> as Host. This will
                        replace the existing Zoom meeting associated with this class. The schedule and roster for this
                        class will <b>not</b> be changed.
                    </Trans>
                </p>
                <p>{t('change-host-confirmation-text')}</p>
            </StyledDialogContent>

            <DialogActions>
                <Button variant="outlined" onClick={handleClose}>
                    {t('Cancel')}
                </Button>
                <StyledLoadingButton onClick={onSubmit} color="error" variant="contained" loading={isLoading}>
                    {t('change-host')}
                </StyledLoadingButton>
            </DialogActions>
        </StyledDialog>
    );
};

export { RosterChangeHostModal };
